import {ActivityLineChartProvider} from "./activityLineChartProvider";
import {ActivityDto} from "../../../dtos/activity.dto";
import {ActivityAnalysisServiceResponseDto} from "../../../dtos/statistics/activityAnalysisServiceResponseDto";
import {ProgressOverTimeFilter} from "../../../viewmodels/progressOverTimeFilter";
import {I18nService} from "../../../services/i18n/i18n.service";
import {Label} from "ng2-charts";
import {Activities} from "../../../utils/activities";
import {format} from "date-fns";
import {ProgressOverTimeMetric} from "../../../viewmodels/progressOverTimeMetric";
import {ChartDataSets} from "chart.js";
import {ProgressOverTimeMetricType} from "../../../viewmodels/progressOverTimeMetricType";
import {BodyWeightActivityResultsModel} from "../../../dtos/statistics/bodyWeightActivityResultsModel";
import {ChartUtils} from "../../../utils/chartUtils";
import {Value} from "../../../utils/values/value";
import {DateFormat, Dates, KeyValue} from "common";

export class BodyWeightLineChartProvider extends ActivityLineChartProvider {
    private datasets: Chart.ChartDataSets[];

    public getDatasets(activities: ActivityDto[], analysis: ActivityAnalysisServiceResponseDto, filter: ProgressOverTimeFilter, chartOptions: Chart.ChartOptions, i18n: I18nService): KeyValue<Label[], Chart.ChartDataSets[]> {
        const activitiesByDay = Activities.groupByDay(activities);

        const days = Array.from(activitiesByDay.keys()).sort();
        const labels: Label[] = days.map(day => format(day, Dates.getFormat(DateFormat.DATE_SHORTER)));

        this.datasets = this.createDatasets(activitiesByDay, analysis, filter.metric, days, i18n);

        let maxValue = 0;
        for (const dataset of this.datasets) {
            maxValue = Math.max(0, ...(dataset.data as number[]).filter(value => !Number.isNaN(value)));
        }

        // Chart options
        if (!chartOptions.plugins) {
            chartOptions.plugins = {};
        }
        chartOptions.plugins.datalabels = {
            formatter: this.labelFormatter.bind(this),
            anchor: 'end',
            align: 'end',
        };

        chartOptions.scales.yAxes[0].ticks = {min: 0, max: maxValue * 1.2};

        return KeyValue.of(labels, this.datasets);
    }

    private createDatasets(activitiesPerDay: Map<number, ActivityDto[]>, analysis: ActivityAnalysisServiceResponseDto, metric: ProgressOverTimeMetric, days: number[], i18n: I18nService): Chart.ChartDataSets[] {
        const dataset: ChartDataSets = {data: [], label: i18n.text(metric.title), spanGaps: true, lineTension: 0};
        ChartUtils.formatDatasetPrimary(dataset);

        for (let i = 0; i < days.length; i++) {
            const day = days[i];
            let value = NaN;

            // Create values for metrics.
            // Add NaN when there is no value for the day
            if (activitiesPerDay.has(day)) {
                const activities = activitiesPerDay.get(day);
                const activitiesToResults = this.combineActivitiesToResults<BodyWeightActivityResultsModel>(activities, analysis);
                const results = activities.map(a => activitiesToResults.get(a));
                value = this.getMetricForResults(results, metric.type);
            }
            dataset.data.push(value);
        }

        return Array.of(dataset);
    }

    private getMetricForResults(results: BodyWeightActivityResultsModel[], type: ProgressOverTimeMetricType) {
        let value = NaN;
        switch (type) {
            case ProgressOverTimeMetricType.MAX_VALUE:
                value = Math.max(0, ...results.map(r => r.weight));
                break;
        }
        return value;
    }

    private labelFormatter(value: number): string {
        // default to integer
        let formatted: string = value.toFixed(0);

        // find the value in the datasets
        let found = false;
        for (const dataset of this.datasets) {
            for (const datum of dataset.data) {
                if (datum === value) {
                    formatted = new Value(value).format(1);
                    found = true;
                    break;
                }
            }
            if (found) {
                break;
            }
        }
        return formatted;
    }
}
